/**
 * Iter-239 — Goal Hit Activity Feed
 *
 * Embedded into ClientInsightsRollup. Shows recent creator achievements as
 * a Slack-style feed so executives feel the campaign's momentum.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Trophy, Sparkles } from 'lucide-react';

const money = (cents) =>
  cents == null ? '—' : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function GoalHitFeed({ clientId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/creator-bonuses/goal-hits?client_id=${clientId}`);
      setEvents(r.data?.events || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (loading || events.length === 0) return null;

  return (
    <section className="mt-8" data-testid="goal-hit-feed">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-amber-500" />
        <h2 className="text-lg font-bold text-slate-900">Recent Creator Wins</h2>
        <span className="text-xs text-slate-500">· Live celebration feed</span>
      </div>
      <ul className="space-y-2">
        {events.slice(0, 8).map((e, i) => (
          <li key={e.id}
            className="bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-xl p-3 flex items-center gap-3"
            data-testid={`goal-hit-${i}`}>
            <div className="text-2xl">🏆</div>
            <div className="flex-1">
              <div className="text-sm text-slate-900">
                <b>{e.creator_name}</b> just earned a{' '}
                <span className="font-bold text-amber-700">{money(e.bonus_cents)}</span> bonus
              </div>
              <div className="text-xs text-slate-500">
                {e.tier_label} · {e.snapshot_value.toLocaleString()}{' '}
                {e.metric === 'revenue_cents' ? 'in revenue' : e.metric}
                {' · '}{timeAgo(e.created_at)}
                {e.email_sent_at && ' · email sent'}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
